import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { toast } from "sonner";
import {
  AlertTriangle,
  Clipboard,
  Loader2,
  RefreshCw,
  ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ADMIN_EMAILS } from "../config/staffConfig";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * BM-H247 F-04 (02/09/2026) — Bonsai Mail Failure Console.
 *
 * Audit 07b documented six classes of failure that already land in
 * `bonsai_audit_logs` with zero FE readers:
 *   1. status="failed"                          (Graph upload failed)
 *   2. status="refused"                         (attendances missing / lookup errored)
 *   3. status="legacy_matter_root_fallback"     (legacy matter, filed to root)
 *   4. per-attachment failures (audit rows per attachment upload)
 *   5. cross-user _audit_copy_row rows
 *   6. metadata stamp failed (extra.metadata_stamp_error)
 *
 * This console reads:
 *   GET /api/bonsai/filing/admin/filing-failures-summary?since_hours=24
 *   GET /api/bonsai/filing/admin/filing-failures?...
 *
 * Two tabs:
 *   • Failures — status ∈ (failed, refused)
 *   • Warnings — status = legacy_matter_root_fallback
 *
 * Filters: user_email substring, matter_ref substring, filename substring,
 * time window (24 h / 7 d / 30 d).
 *
 * Access is gated by ADMIN_EMAILS. Non-admin sessions render a 403 panel.
 * Read-only.
 */

const WINDOW_OPTIONS = [
  { hours: 24, label: "24 hours" },
  { hours: 168, label: "7 days" },
  { hours: 720, label: "30 days" },
];

const STATUS_TONE = {
  failed: "text-red-700 bg-red-50 border-red-200",
  refused: "text-red-700 bg-red-50 border-red-200",
  legacy_matter_root_fallback:
    "text-amber-700 bg-amber-50 border-amber-200",
};

function isCurrentUserAdmin() {
  const userId = (sessionStorage.getItem("bonsai_user_id") || "").toLowerCase();
  if (!userId) return false;
  return ADMIN_EMAILS.has(userId);
}

function fmtTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const cls =
    STATUS_TONE[status] || "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}
    >
      {status}
    </span>
  );
}

function CorrelationCell({ row }) {
  // BM-H247 F-04: "correlation_id" per Audit 07b lives across a handful of
  // fields depending on the writer — prefer the explicit correlation_id,
  // fall back to internet_message_id, then to filename+timestamp.
  const cid =
    row.correlation_id ||
    row.internet_message_id ||
    (row.filename && row.timestamp ? `${row.filename}@${row.timestamp}` : "");
  const short = cid ? String(cid).slice(0, 14) + (cid.length > 14 ? "…" : "") : "—";
  const copy = () => {
    if (!cid) return;
    try {
      navigator.clipboard.writeText(cid);
      toast.success("Correlation id copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-xs"
      title={cid || "no correlation id"}
      disabled={!cid}
    >
      <Clipboard className="w-3 h-3" />
      <span className="font-mono">{short}</span>
    </button>
  );
}

export default function BonsaiMailFailureConsole() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("failures"); // 'failures' | 'warnings'
  const [sinceHours, setSinceHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    user_email: "",
    matter_ref: "",
    filename_contains: "",
  });

  const userId = sessionStorage.getItem("bonsai_user_id") || "";
  const allowed = isCurrentUserAdmin();

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError("");
    try {
      const includeWarnings = tab === "warnings";
      const params = new URLSearchParams();
      params.set("limit", "200");
      params.set("since_hours", String(sinceHours));
      params.set("include_warnings", includeWarnings ? "true" : "false");
      if (filters.user_email) params.set("user_email", filters.user_email);
      if (filters.matter_ref) params.set("matter_ref", filters.matter_ref);
      if (filters.filename_contains)
        params.set("filename_contains", filters.filename_contains);

      const [summaryRes, rowsRes] = await Promise.all([
        axios.get(
          `${API}/api/bonsai/filing/admin/filing-failures-summary?since_hours=${sinceHours}&include_warnings=true`,
          { headers: { "X-Bonsai-User-Id": userId } },
        ),
        axios.get(
          `${API}/api/bonsai/filing/admin/filing-failures?${params.toString()}`,
          { headers: { "X-Bonsai-User-Id": userId } },
        ),
      ]);
      setSummary(summaryRes.data || null);

      // Client-side split: on warnings tab keep only fallback rows;
      // on failures tab keep failed/refused only (include_warnings=false
      // already excludes them, but defence-in-depth in case the endpoint
      // widens in future).
      //
      // BM-H249: /admin/filing-failures now emits an AddinResult envelope
      // ({ ok, code, data: { rows, ... }, ... }). Support both shapes so
      // this page keeps working during rollout and if the endpoint is
      // ever un-migrated.
      const rowsBody = rowsRes.data || {};
      const rowsPayload = (rowsBody && rowsBody.contract_version) ? (rowsBody.data || {}) : rowsBody;
      const allRows = rowsPayload.rows || [];
      setRows(
        includeWarnings
          ? allRows.filter((r) => r.status === "legacy_matter_root_fallback")
          : allRows.filter((r) => r.status === "failed" || r.status === "refused"),
      );
    } catch (e) {
      const msg = errorMessage(e, "Failed to load failures");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [allowed, filters, sinceHours, tab, userId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sinceHours]);

  const totals = summary?.totals || {};
  const byUser = summary?.by_user || [];

  const failuresCount =
    (totals.failed || 0) + (totals.refused || 0);
  const warningsCount = totals.legacy_matter_root_fallback || 0;

  const activeRows = useMemo(() => rows, [rows]);

  if (!allowed) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="border border-red-200 bg-red-50 rounded-lg p-6 flex items-start gap-3">
          <ShieldOff className="w-5 h-5 text-red-700 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">Admins only</div>
            <div className="text-sm text-red-700 mt-1">
              This console is restricted to Bonsai Law administrators.
              You are signed in as {userId || "an unrecognised account"}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Bonsai Mail — Failure Console
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Live view of the six failure classes documented in Audit 07b.
            Read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border rounded-md px-2 py-1 text-sm"
            value={sinceHours}
            onChange={(e) => setSinceHours(Number(e.target.value))}
          >
            {WINDOW_OPTIONS.map((w) => (
              <option key={w.hours} value={w.hours}>
                Last {w.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-md p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      ) : null}

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-slate-500">Failures</div>
          <div className="text-2xl font-semibold text-red-700">
            {failuresCount}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            status = failed + refused
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-slate-500">Warnings</div>
          <div className="text-2xl font-semibold text-amber-700">
            {warningsCount}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            legacy matter root fallback
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-xs text-slate-500">Users affected</div>
          <div className="text-2xl font-semibold text-slate-900">
            {byUser.length}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            in the selected window
          </div>
        </div>
      </div>

      {/* By-user breakdown */}
      {byUser.length > 0 ? (
        <div className="border rounded-lg bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">User</th>
                <th className="text-right px-3 py-2 font-medium">Failed</th>
                <th className="text-right px-3 py-2 font-medium">Refused</th>
                <th className="text-right px-3 py-2 font-medium">Warnings</th>
              </tr>
            </thead>
            <tbody>
              {byUser.map((row) => (
                <tr key={row.user_email} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.user_email}
                  </td>
                  <td className="px-3 py-2 text-right text-red-700">
                    {row.failed}
                  </td>
                  <td className="px-3 py-2 text-right text-red-700">
                    {row.refused}
                  </td>
                  <td className="px-3 py-2 text-right text-amber-700">
                    {row.warnings}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Filters */}
      <div className="border rounded-lg bg-white p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          className="border rounded px-2 py-1 text-sm"
          placeholder="User email"
          value={filters.user_email}
          onChange={(e) =>
            setFilters((f) => ({ ...f, user_email: e.target.value }))
          }
        />
        <input
          className="border rounded px-2 py-1 text-sm"
          placeholder="Matter reference"
          value={filters.matter_ref}
          onChange={(e) =>
            setFilters((f) => ({ ...f, matter_ref: e.target.value }))
          }
        />
        <input
          className="border rounded px-2 py-1 text-sm"
          placeholder="Filename contains"
          value={filters.filename_contains}
          onChange={(e) =>
            setFilters((f) => ({ ...f, filename_contains: e.target.value }))
          }
        />
        <Button size="sm" onClick={load} disabled={loading}>
          Apply filters
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        {[
          { id: "failures", label: `Failures (${failuresCount})` },
          { id: "warnings", label: `Warnings (${warningsCount})` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              tab === t.id
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Rows */}
      <div className="border rounded-lg bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-medium">When</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">User</th>
              <th className="text-left px-3 py-2 font-medium">Matter</th>
              <th className="text-left px-3 py-2 font-medium">Filename</th>
              <th className="text-left px-3 py-2 font-medium">Error</th>
              <th className="text-left px-3 py-2 font-medium">Ref</th>
            </tr>
          </thead>
          <tbody>
            {loading && activeRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                  Loading…
                </td>
              </tr>
            ) : activeRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No rows in the selected window.
                </td>
              </tr>
            ) : (
              activeRows.map((r, idx) => (
                <tr
                  key={`${r.timestamp}-${r.filename}-${idx}`}
                  className="border-t align-top"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">
                    {fmtTimestamp(r.timestamp)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.user_email || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.matter_ref || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.filename || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-red-700 max-w-md">
                    {r.error || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <CorrelationCell row={r} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-400">
        Source: bonsai_audit_logs. Written by audit_graph_upload across
        _file_email_inner, save_attachments_to_folder, and file_on_send.
        Read-only console; to remediate a row, open the affected email in
        Bonsai Mail and re-file.
      </div>
    </div>
  );
}
